import React, { useState, useMemo, useCallback } from 'react';
import { Search, Globe, X } from 'lucide-react';
import * as Flags from 'country-flag-icons/react/3x2';
import PropTypes from 'prop-types';

const CountryList = ({
  countries = [],
  selectedCountry = null,
  onCountrySelect,
  onRequestClose = null,
  helperText = null,
  variant = 'default',
  titleId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const isBottomSheet = variant === 'bottomSheet';
  const isModal = variant === 'modal';

  const getCountryFlag = useCallback((country) => {
    if (!country?.iso2_code) {
      return (
        <div className="w-6 h-4 bg-gray-200 rounded-sm flex items-center justify-center">
          <span className="text-xs text-gray-500 font-mono">{country?.iso_code || '??'}</span>
        </div>
      );
    }

    const FlagComponent = Flags[country.iso2_code];
    if (FlagComponent) {
      return <FlagComponent className="w-6 h-4 rounded-sm shadow-md" />;
    }

    return (
      <div className="w-6 h-4 bg-gray-200 rounded-sm flex items-center justify-center">
        <span className="text-xs text-gray-500 font-mono">{country.iso_code}</span>
      </div>
    );
  }, []);

  const filteredCountries = useMemo(() => {
    if (!countries || !Array.isArray(countries)) return [];

    return countries.filter((country) => {
      const matchesSearch =
        country.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.iso_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.iso2_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.capital?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [countries, searchTerm]);

  const handleCountryClick = useCallback(
    (country) => {
      onCountrySelect?.(country);
    },
    [onCountrySelect]
  );

  const CountryCard = ({ country, index }) => (
    <div
      key={country.iso_code || index}
      className={`country-card-simple group ${
        selectedCountry?.iso_code === country.iso_code ? 'country-card-active-simple' : ''
      } hover:bg-gray-100 transition-all duration-200`}
      onClick={() => handleCountryClick(country)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getCountryFlag(country)}
          <h3 className="text-sm font-medium text-gray-900">{country.name}</h3>
        </div>
        <span className="text-xs text-gray-500 font-mono">{country.iso_code}</span>
      </div>
    </div>
  );

  const titleRowClass = `flex items-center ${
    onRequestClose ? 'justify-between gap-2' : 'space-x-2'
  } ${helperText ? 'mb-2' : 'mb-4'}`;

  const outerClass = isBottomSheet
    ? 'flex h-full min-h-0 min-w-0 flex-1 flex-col bg-white border-0 rounded-none'
    : isModal
      ? 'flex h-full min-h-0 w-full flex-col bg-white'
      : 'h-full flex flex-col border-2 border-blue-200 rounded-lg bg-white';

  return (
    <div className={outerClass}>
      <div className="shrink-0 border-b border-gray-200 p-4">
        <div className={titleRowClass}>
          <div className="flex min-w-0 items-center space-x-2">
            <Globe className="h-4 w-4 flex-shrink-0 text-blue-500" />
            <h2 id={titleId} className="truncate text-base font-semibold text-gray-900">
              Countries
            </h2>
            <span className="flex-shrink-0 text-xs text-gray-500">({countries?.length || 0})</span>
          </div>
          {onRequestClose && (
            <button
              type="button"
              onClick={onRequestClose}
              className="flex-shrink-0 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label="Close countries list"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {helperText && <p className="mb-3 text-xs text-gray-500">{helperText}</p>}

        <div className="relative">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-full border border-gray-200 bg-white py-3 pl-4 pr-10 text-base shadow-inner focus:border-transparent focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {filteredCountries.length > 0 ? (
          <div className="space-y-1 p-2">
            {filteredCountries.map((country, index) => (
              <CountryCard key={country.iso_code || index} country={country} index={index} />
            ))}
          </div>
        ) : (
          <div
            className={
              isModal
                ? 'flex min-h-full flex-col items-center justify-center p-4 text-center text-gray-500'
                : 'p-4 text-center text-gray-500'
            }
          >
            <p className="text-sm">No countries found</p>
            {searchTerm && <p className="mt-1 text-xs">Try a different search term</p>}
          </div>
        )}
      </div>
    </div>
  );
};

CountryList.propTypes = {
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      iso_code: PropTypes.string.isRequired,
      iso2_code: PropTypes.string,
      name: PropTypes.string.isRequired,
      region: PropTypes.string,
      latitude: PropTypes.string,
      longitude: PropTypes.string,
      population: PropTypes.string,
      capital: PropTypes.string,
    })
  ),
  selectedCountry: PropTypes.shape({
    iso_code: PropTypes.string,
    name: PropTypes.string,
  }),
  onCountrySelect: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func,
  helperText: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'bottomSheet', 'modal']),
  titleId: PropTypes.string,
};

export default CountryList;
